import { useRef, useState, useEffect } from "react";
import { useClickOutside } from "../../../hooks/useClickOutside";
import tooltipStyles from "../../../styles/tooltip.module.css";

const ClickableTooltip = ({
  icon,
  children,
  classes
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  classes?: string;
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isTooltipPositionedTop, setTooltipPositionedTop] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    setIsTooltipVisible((prev) => !prev);
  };

  useClickOutside(tooltipRef, () => setIsTooltipVisible(false));

  /* This useEffect handles tooltip visibility:
     - Prevents page scrolling when the tooltip is visible
     - Determines if the tooltip should be positioned top or bottom of the icon based on available space */
  useEffect(() => {
    if (isTooltipVisible) {
      document.body.classList.add("overflow-hidden");

      if (tooltipRef.current && optionsRef.current) {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const optionsRect = optionsRef.current.getBoundingClientRect();
        const availableSpaceBelow = window.innerHeight - tooltipRect.bottom;
        const availableSpaceAbove = tooltipRect.top;
        if (
          availableSpaceBelow < optionsRect.height &&
          availableSpaceAbove > optionsRect.height
        ) {
          setTooltipPositionedTop(true);
        } else {
          setTooltipPositionedTop(false);
        }
      }
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    // remove added style when component unmounts
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isTooltipVisible]);

  return (
    <div
      className={tooltipStyles.tooltipWrapper}
      onClick={handleClick}
      ref={tooltipRef}
    >
      <div className={tooltipStyles.tooltipIcon}>{icon}</div>
      {isTooltipVisible && (
        <div
          ref={optionsRef}
          className={`${tooltipStyles.tooltipOnClick} ${
            isTooltipPositionedTop ? tooltipStyles.tooltipTopPosition : ""
          } ${classes || ""}`}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default ClickableTooltip;
